/* eslint-disable @typescript-eslint/no-explicit-any */
import { localSidebarKey, localKey, localOffset } from "@/dto/local.key.dto";
import { globals } from "@/main";
import { setCurrentPath } from "@/services/utils/helper.util";
import { LooseRequired } from "@vue/shared";
import { defineComponent, ref, computed, ComponentCustomProperties } from "vue";

class ModeChanger {
  globals: any;
  applicationMode: any;
  footerSidebars: any;

  constructor(globals: any, applicationMode: any, footerSidebars: any) {
    this.globals = globals;
    this.applicationMode = applicationMode;
    this.footerSidebars = footerSidebars;
  }

  changeMode() {
    this.globals.$helper.changeMode();
    this.updateMode();
    this.updateFooter();
    this.commitMode();
  }

  updateMode() {
    this.applicationMode.value = this.globals.$helper.getMode();
  }

  updateFooter() {
    const isLightMode = this.applicationMode.value === "light-mode";
    this.footerSidebars.value[0] = {
      icon: require(`@/assets/icons/sidebars/switch-${
        isLightMode ? "mode-setting" : "dark-mode"
      }.svg`),
      title: isLightMode ? "sidebarLightMode" : "sidebarDarkMode",
      params: "",
    };
  }

  commitMode() {
    this.globals.$store.commit("storeAppMode", this.applicationMode.value);
  }
}

class Sidebar {
  globals: ComponentCustomProperties & Record<string, any>;
  props: LooseRequired<{
    readonly loading: boolean;
    readonly style: string;
    readonly class: string;
    readonly active: string;
    readonly params: string;
  }>;

  className: any;
  styleName: any;
  keySearch: any;
  dataResults: any;
  isLogout: any;
  applicationMode: any;
  footerSidebars: any;
  menus: any;
  modeChanger: ModeChanger;

  constructor(
    globals: ComponentCustomProperties & Record<string, any>,
    props: LooseRequired<{
      readonly loading: boolean;
      readonly style: string;
      readonly class: string;
      readonly active: string;
      readonly params: string;
    }>
  ) {
    this.globals = globals;
    this.props = props;
    this.className = ref(props.class);
    this.styleName = ref(props.style);
    this.keySearch = ref("");
    this.dataResults = ref([]);
    this.isLogout = ref(false);
    this.applicationMode = ref(this.globals.$helper.getMode());
    this.footerSidebars = ref([this.createFooterSidebar()]);

    this.menus = ref(
      globals.$store?.state?.menus ||
        globals.$helper?.getSidebars?.value.data ||
        []
    );

    this.modeChanger = new ModeChanger(
      this.globals,
      this.applicationMode,
      this.footerSidebars
    );
  }

  createFooterSidebar() {
    const isLightMode = this.applicationMode.value === "light-mode";
    return {
      icon: require(`@/assets/icons/sidebars/switch-${
        isLightMode ? "mode-setting" : "dark-mode"
      }.svg`),
      title: isLightMode ? "sidebarLightMode" : "sidebarDarkMode",
      params: "",
    };
  }

  getActive(
    value: string,
    route: { path: string },
    sidebar: { code: string; children: { children?: any[] | undefined }[] }
  ) {
    const url = value.split("/");
    const path = route?.path.split("/");
    const checked = url[1] === path[1];

    if (
      checked ||
      (!checked && sidebar?.code === "DASHBOARD" && path[1] === "")
    ) {
      this.globals.$store.commit(
        "storeCurrentPath",
        setCurrentPath(sidebar?.children) || []
      );
    }

    return (
      checked || (!checked && sidebar?.code === "DASHBOARD" && path[1] === "")
    );
  }

  toggleSidebar(value: string) {
    const width = value === "fix-sidebar" ? "fix-sidebar-min" : "fix-sidebar";
    localStorage.setItem(localSidebarKey, width);
    globals.$helper.getSidebars.value = {
      data: globals.$store?.state?.menus,
      width: width,
    };
  }

  handleLogout(value: { title: string }) {
    if (value.title === "sidebarLogout") {
      this.isLogout.value = true;
    }

    this.modeChanger.changeMode();
    return value;
  }

  onLogout() {
    this.isLogout.value = false;
    localStorage.removeItem(localKey?.accessToken);
    localStorage.removeItem(localKey?.refreshToken);
    location.reload();
  }

  cancelLogout() {
    this.isLogout.value = false;
  }

  onSidebarClick() {
    localStorage.setItem(localOffset, String(0));
    this.globals.$helper.setStateFilter("");
  }

  get isLoading() {
    return computed(() => this.props.loading);
  }

  changeMode() {
    this.modeChanger.changeMode();
  }

  isLogoutFunc() {
    this.changeMode();
  }

  onSearch() {
    const allMenus =
      globals.$store?.state?.menus ||
      globals.$helper?.getSidebars?.value.data ||
      [];

    const searchTerm = this.keySearch.value.toLowerCase();
    this.menus.value = allMenus.filter(
      (menu: any) =>
        menu.title.toLowerCase().includes(searchTerm) ||
        menu.titleForeign.toLowerCase().includes(searchTerm)
    );
  }

  toggleSidebarActive() {
    const width = "fix-sidebar";
    localStorage.setItem(localSidebarKey, width);
    globals.$helper.getSidebars.value = {
      data: globals.$store?.state?.menus,
      width: width,
    };
  }
}

export default defineComponent({
  name: "c-sidebar",

  props: {
    loading: { type: Boolean, default: false },
    style: { type: String, default: "" },
    class: { type: String, default: "" },
    active: { type: String, default: "" },
    params: { type: String, default: "" },
  },

  setup(props) {
    const sidebar = new Sidebar(globals, props);

    return {
      menus: sidebar.menus,
      className: sidebar.className,
      styleName: sidebar.styleName,
      isLoading: sidebar.isLoading,
      keySearch: sidebar.keySearch,
      onSearch: () => sidebar.onSearch(),
      getActive: (value: any, route: any, sidebarItem: any) =>
        sidebar.getActive(value, route, sidebarItem),
      footerSidebars: sidebar.footerSidebars,
      toggleSidebar: (value: any) => sidebar.toggleSidebar(value),
      isLogout: sidebar.isLogout,
      onLogout: () => sidebar.onLogout(),
      cancelLogout: () => sidebar.cancelLogout(),
      handleLogout: (value: any) => sidebar.handleLogout(value),
      onSidebarClick: () => sidebar.onSidebarClick(),
      applicationMode: sidebar.applicationMode,
      changeMode: () => sidebar.changeMode(),
      isLogoutFunc: () => sidebar.isLogoutFunc(),
      toggleSidebarActive: () => sidebar.toggleSidebarActive(),
    };
  },
});
